// Opens the /linux page as an internal tab. Shared by the start tile, the Worksheets entry, the
// command palette and favorites. One Linux tab is plenty (a machine can only run in one tab), so an
// existing one is focused. `boot` leaves a short-lived note in sessionStorage (shared with our
// same-origin iframe) so the page starts the default machine at once: one click, Linux boots. A
// restored session never carries the note, so it never boots a machine by surprise.
import { state } from "./state.js";
import { openInternal, selectTab } from "./tabs.js";
import { frames } from "./view.js";

export const LINUX_PAGE = "/linux";
export const isLinuxTab = (t) => !!t && t.internal === LINUX_PAGE;

export function openLinux(boot = true) {
	const existing = state.tabs.find(isLinuxTab);
	if (existing) { selectTab(existing.id); return existing.id; }
	if (boot) { try { sessionStorage.setItem("revault:linux-autoboot", String(Date.now())); } catch (e) {} }
	return openInternal(LINUX_PAGE, "Linux");
}

// Internal pages a Worksheets/favorites entry may open (first-party data, still allowlisted).
export function openAppPage(page, title) {
	if (page === LINUX_PAGE) return openLinux(true);
	if (page === "/tools" || page === "/help" || /^\/tools\/[a-z0-9-]{1,40}$/.test(page)) return openInternal(page, title || "Page");
	return null;
}

// Name of the machine running in a Linux tab (asks the same-origin page), or null.
export function linuxBusyIn(tab) {
	if (!isLinuxTab(tab)) return null;
	try {
		const w = frames.get(tab.id) && frames.get(tab.id).contentWindow;
		return (w && typeof w.revaultLinuxBusy === "function" && w.revaultLinuxBusy()) || null;
	} catch (e) { return null; }
}
export const linuxBusy = () => { for (const t of state.tabs) { const n = linuxBusyIn(t); if (n) return n; } return null; };

// What a first-party page would lose if closed now, in its own words (Settings holding a carry
// code's last open), or null. The page names it through window.revaultUnsaved().
export function unsavedIn(tab) {
	if (!tab || !tab.internal) return null;
	try {
		const w = frames.get(tab.id) && frames.get(tab.id).contentWindow;
		return (w && typeof w.revaultUnsaved === "function" && w.revaultUnsaved()) || null;
	} catch (e) { return null; }
}
export const anyUnsaved = () => { for (const t of state.tabs) { const n = unsavedIn(t); if (n) return n; } return null; };

// Leaving a Linux tab (close, reload, replace) stops its machine and loses anything since its last
// save, so every such path asks the same question first. A page with something unsaved asks its
// own question the same way. Tabs with neither pass.
export function leaveLinuxOk(tab) {
	const name = linuxBusyIn(tab);
	if (name && !confirm(name + " is still running. Leaving stops it, and anything since its last save is lost. Continue?")) return false;
	const lost = unsavedIn(tab);
	if (!lost) return true;
	if (!confirm(lost)) return false;
	window.__rvLeaveOk = Date.now(); // the page skips its own "leave?" dialog for this one
	return true;
}

// Favorites of Worksheets entries that open an internal page (e.g. Linux) carry `page`.
export const favoritePage = (f) => (f && typeof f.page === "string" ? f.page : null);
